/**
 * Custom hook for managing attendance data with services, queries, and mutations
 */

import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import type {
  Dochadzka,
  AttendanceFilters,
  AttendanceSummary,
  AttendanceFormDataWithServices,
} from "../types";
import { sortAttendanceByDateTime, calculateTotalDuration } from "../utils";

export function useAttendanceDataWithServices(filters: AttendanceFilters) {
  const { t } = useTranslation();
  const { selectedKlientId, startDate, endDate } = filters;

  // Fetch clients list
  const { data: klienti } = trpc.klient.list.useQuery();

  // Fetch filtered attendance data
  const { data: filteredDochadzka, isLoading } =
    trpc.dochadzka.getByDateRange.useQuery({
      startDate,
      endDate,
      klientId: selectedKlientId || undefined,
    });

  // Fetch all attendance data (for print all clients)
  const { data: allDochadzka } = trpc.dochadzka.getByDateRange.useQuery(
    {
      startDate,
      endDate,
      klientId: undefined,
    },
    {
      enabled: false, // Don't fetch by default
    }
  );

  // Mutations
  const createMutation = trpc.dochadzka.createWithServices.useMutation();
  const updateMutation = trpc.dochadzka.updateWithServices.useMutation();
  const deleteMutation = trpc.dochadzka.delete.useMutation();
  const utils = trpc.useUtils();

  // Sorted attendance data
  const sortedDochadzka = useMemo(() => {
    if (!filteredDochadzka) return [];
    return sortAttendanceByDateTime(filteredDochadzka);
  }, [filteredDochadzka]);

  // Calculate summary statistics
  const summary: AttendanceSummary = useMemo(() => {
    const hasIncomeBreakdown = sortedDochadzka.some(
      attendance => attendance.income !== undefined
    );

    return {
      totalDuration: calculateTotalDuration(sortedDochadzka),
      totalPrice: sortedDochadzka.reduce((total, attendance) => {
        if (attendance.income) return total + attendance.income.selectedGross;
        return total + Number(attendance.cena ?? 0);
      }, 0),
      totalProfit: hasIncomeBreakdown
        ? sortedDochadzka.reduce(
            (total, attendance) =>
              total + (attendance.income?.selectedProfit ?? 0),
            0
          )
        : undefined,
    };
  }, [sortedDochadzka]);

  // Create attendance record with services
  const createAttendance = async (data: AttendanceFormDataWithServices) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t("attendance:messages.attendanceCreated"));
      utils.dochadzka.getByDateRange.invalidate();
      return true;
    } catch (error) {
      console.error("Create attendance error:", error);
      toast.error(t("common:error"));
      return false;
    }
  };

  // Update attendance record with services
  const updateAttendance = async (
    id: number,
    data: AttendanceFormDataWithServices
  ) => {
    try {
      await updateMutation.mutateAsync({ id, ...data });
      toast.success(t("attendance:messages.attendanceUpdated"));
      utils.dochadzka.getByDateRange.invalidate();
      return true;
    } catch (error) {
      console.error("Update attendance error:", error);
      toast.error(t("common:error"));
      return false;
    }
  };

  // Delete attendance record
  const deleteAttendance = async (id: number) => {
    if (!confirm(t("attendance:messages.deleteConfirm"))) {
      return false;
    }

    try {
      await deleteMutation.mutateAsync({ id });
      toast.success(t("attendance:messages.attendanceDeleted"));
      utils.dochadzka.getByDateRange.invalidate();
      return true;
    } catch (error) {
      toast.error(t("common:error"));
      return false;
    }
  };

  return {
    // Data
    klienti,
    sortedDochadzka,
    allDochadzka,
    summary,
    isLoading,

    // Mutations
    createAttendance,
    updateAttendance,
    deleteAttendance,
  };
}
